import React, { useMemo, useState } from 'react';
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Typography } from '@mui/material';
import { SmartDisplay as SmartDisplayIcon } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';

const BADMINTON_PHYSICAL_BENCHMARKS_BY_REGION = {
  England: [
    {
      test: '30-15 Intermittent Fitness Test (IFT)',
      unit: 'km·h^-1',
      u19: 'F:20 | M:22',
      u17: 'F:20 | M:22',
      u15: 'U15 F:18 | M:20',
      seniorMin: 'F:21 | M:23',
      seniorTarget: 'F:22 | M:24',
      youtubeId: 'ifnFVfglLw4',
      youtubeCaption: '30-15 Intermittent Fitness Test demo and protocol (use VIFT for interval prescription).',
      citation: '',
    },
    {
      test: 'Single-leg Isometric Test',
      unit: 'xBW',
      u19: 'F:4.0 | M:4.25',
      u17: 'F:3.5 | M:4.0',
      u15: 'Maturity-tested; not for selection',
      seniorMin: 'F:4.0 | M:4.5',
      seniorTarget: 'F:4.5 | M:5.0',
      youtubeId: '7Udb4E3Uks8',
      youtubeCaption: 'Single-leg isometric test protocol and safe setup (force-plate/VALD guidance).',
      citation: '',
    },
    {
      test: 'Countermovement Jump (CMJ)',
      unit: 'cm',
      u19: 'F:40 | M:50',
      u17: 'F:35 | M:45',
      u15: 'U15 F:35 | M:40 | U13:25',
      seniorMin: 'F:38 | M:48',
      seniorTarget: 'F:42 | M:55',
      youtubeId: 'm7-AV0mikkI',
      youtubeCaption: 'Countermovement jump how-to and force-plate analysis (hands on hips vs arm swing).',
      citation: '',
    },
  ],
} as const;

export function BadmintonEnglandPhysicalBenchmarkTable() {
  const regionNames = useMemo(
    () => Object.keys(BADMINTON_PHYSICAL_BENCHMARKS_BY_REGION) as Array<keyof typeof BADMINTON_PHYSICAL_BENCHMARKS_BY_REGION>,
    []
  );
  const [selectedRegion, setSelectedRegion] = useState<(typeof regionNames)[number]>(regionNames[0]);
  const [activeVideo, setActiveVideo] = useState<{ id: string; title: string } | null>(null);
  const rows = BADMINTON_PHYSICAL_BENCHMARKS_BY_REGION[selectedRegion];

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Physical standards vary by country and region. Use the tabs below to switch benchmark references.
      </Typography>
      <Tabs
        value={selectedRegion}
        onChange={(_, value) => setSelectedRegion(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 1, minHeight: 36, '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontWeight: 700 } }}
      >
        {regionNames.map((region) => (
          <Tab key={region} value={region} label={region} />
        ))}
      </Tabs>
      <TableContainer sx={{ border: '1px solid #dbe8f6', borderRadius: 1, bgcolor: '#fff' }}>
        <Table size="small" sx={{ minWidth: 920 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f7fbff' }}>
              <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Test</TableCell>
              <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Unit</TableCell>
              <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Junior U19 Target</TableCell>
              <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Junior U17 Target</TableCell>
              <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>U15 Guideline / Target</TableCell>
              <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Senior Min</TableCell>
              <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Senior Target</TableCell>
              <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Resources</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.test}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.test}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.unit}</TableCell>
                <TableCell>{row.u19}</TableCell>
                <TableCell>{row.u17}</TableCell>
                <TableCell>{row.u15}</TableCell>
                <TableCell>{row.seniorMin}</TableCell>
                <TableCell>{row.seniorTarget}</TableCell>
                <TableCell sx={{ minWidth: 220 }}>
                  <Stack spacing={0.5} alignItems="flex-start">
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<SmartDisplayIcon sx={{ fontSize: 14 }} />}
                      onClick={() => setActiveVideo({ id: row.youtubeId, title: row.test })}
                      sx={{ textTransform: 'none', px: 0, minWidth: 0 }}
                    >
                      Watch
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      {row.youtubeCaption}
                    </Typography>
                    {row.citation ? (
                      <Typography variant="caption" color="text.secondary">
                        {row.citation}
                      </Typography>
                    ) : null}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog
        open={Boolean(activeVideo)}
        onClose={() => setActiveVideo(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          {activeVideo ? `${activeVideo.title} Video` : 'Video'}
          <IconButton
            aria-label="Close video"
            onClick={() => setActiveVideo(null)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {activeVideo ? (
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                pt: '56.25%',
                overflow: 'hidden',
                borderRadius: 1,
                bgcolor: '#000',
              }}
            >
              <Box
                component="iframe"
                src={`https://www.youtube.com/embed/${activeVideo.id}?autoplay=1`}
                title={activeVideo.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 0,
                }}
              />
            </Box>
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
